"use client";

/**
 * EncryptionWelcome Component
 *
 * First step of onboarding - explains why we use encryption and
 * why users need a separate encryption password.
 *
 * Key messaging:
 * - We take privacy seriously
 * - We cannot read your messages (zero-knowledge)
 * - Separate password = your data belongs to you
 * - Recovery codes are critical
 */
import { c as _c } from "react/compiler-runtime";
import React, { useState } from 'react';
type EncryptionWelcomeProps = {
  onContinue: () => void;
  loading?: boolean;
};
export default function EncryptionWelcome(t0) {
  const $ = _c(33);
  const {
    onContinue,
    loading
  } = t0;
  const [understood, setUnderstood] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="text-center mb-8"><div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 mb-6 shadow-lg shadow-emerald-500/25"><svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg></div><h1 className="text-3xl font-bold text-foreground mb-3">Your Privacy Matters to Us</h1><p className="text-lg text-foreground/60">Before we get started, let's set up your message encryption</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2"><svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>Zero-Knowledge Encryption</h2>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-foreground/70 leading-relaxed mb-4">Every message you send is <span className="text-emerald-600 font-semibold">encrypted on your device</span> before it ever reaches our servers. This means:</p>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <li className="flex items-start gap-3"><svg className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg><span><strong className="text-foreground">We cannot read your messages</strong> - not our employees, not hackers, not anyone</span></li>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <li className="flex items-start gap-3"><svg className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg><span><strong className="text-foreground">Your data belongs to you</strong> - only you have the key to unlock it</span></li>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mb-8">{t2}{t3}<ul className="space-y-3 text-foreground/70">{t4}{t5}<li className="flex items-start gap-3"><svg className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg><span><strong className="text-foreground">Enterprise-grade security</strong> - the same encryption used by banks and governments</span></li></ul></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h3 className="text-lg font-semibold text-sky mb-3 flex items-center gap-2"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" /></svg>Why a Separate Password?</h3>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="text-sky font-semibold">encryption password</span>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="mb-8 p-5 bg-sky/10 border border-sky/30 rounded-xl">{t7}<p className="text-foreground/70 leading-relaxed">Your {t8} is different from your login password for a critical reason: it ensures that even if someone gains access to your account, they still can't read your messages without this second password. Think of it as a <span className="text-sky font-semibold">vault within a vault</span>.</p></div>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <h3 className="text-lg font-semibold text-amber-700 mb-3 flex items-center gap-2"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>This Is Important</h3>;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <li className="flex items-start gap-2"><span className="text-amber-600 font-bold">1.</span><span><strong className="text-foreground">Choose a memorable but strong password</strong> - you'll need it each time you log in on a new device</span></li>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <li className="flex items-start gap-2"><span className="text-amber-600 font-bold">2.</span><span><strong className="text-foreground">Save your recovery codes securely</strong> - they're your only backup if you forget your encryption password</span></li>;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="bg-white/80 backdrop-blur-md border border-white/40 rounded-2xl p-8 mb-6">{t6}{t9}<div className="p-5 bg-amber-500/10 border border-amber-500/30 rounded-xl">{t10}<ul className="space-y-2 text-foreground/70">{t11}{t12}<li className="flex items-start gap-2"><span className="text-amber-600 font-bold">3.</span><span><strong className="text-foreground">We cannot recover your data</strong> - if you lose both your password and recovery codes, your messages are permanently inaccessible</span></li></ul></div></div>;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = e => setUnderstood(e.target.checked);
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  if ($[14] !== understood) {
    t15 = <input type="checkbox" checked={understood} onChange={t14} className="mt-1 h-5 w-5 rounded border-foreground/20 bg-white/80 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0" />;
    $[14] = understood;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="text-foreground/80">I understand that my messages will be encrypted with a password that only I know, and that<span className="text-amber-600 font-semibold"> losing both my encryption password and recovery codes means my data cannot be recovered</span>.</span>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] !== t15) {
    t17 = <label className="flex items-start gap-4 cursor-pointer mb-6">{t15}{t16}</label>;
    $[17] = t15;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  const t18 = !understood || loading;
  let t19;
  if ($[19] !== loading) {
    t19 = loading ? <span className="flex items-center justify-center gap-2"><svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Setting up...</span> : "Continue to Set Up Encryption";
    $[19] = loading;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] !== onContinue || $[22] !== t18 || $[23] !== t19) {
    t20 = <button onClick={onContinue} disabled={t18} className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold text-lg hover:from-emerald-400 hover:to-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/25">{t19}</button>;
    $[21] = onContinue;
    $[22] = t18;
    $[23] = t19;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t17 || $[26] !== t20) {
    t21 = <div className="bg-white/80 backdrop-blur-md border border-white/40 rounded-2xl p-6">{t17}{t20}</div>;
    $[25] = t17;
    $[26] = t20;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="flex items-center gap-2"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>AES-256 Encryption</div>;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="flex items-center gap-2"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>Zero-Knowledge</div>;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="mt-8 flex items-center justify-center gap-6 text-sm text-foreground/50">{t22}{t23}<div className="flex items-center gap-2"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" /></svg>Client-Side Only</div></div>;
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  let t25;
  if ($[31] !== t21) {
    t25 = <div className="h-screen overflow-y-auto"><div className="flex items-center justify-center p-4 py-12"><div className="w-full max-w-2xl">{t1}{t13}{t21}{t24}</div></div></div>;
    $[31] = t21;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  return t25;
}